import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getMinimalSupabase } from '../../lib/supabase-minimal';
import { colors } from '../../lib/colors';
import { useAutoSave } from '../../hooks/useQuestionnaireAutoSave';
import { LoadingState } from './LoadingState';
import { ProgressIndicator } from './ProgressIndicator';
import { QuestionStep } from './QuestionStep';
import { ReviewStep } from './ReviewStep';

interface Question {
  id: string;
  type: 'yesno' | 'text' | 'select' | 'date' | 'consent';
  title: string;
  description?: string;
  required?: boolean;
  options?: string[];
  validation?: { 
    pattern?: string; 
    minLength?: number; 
    maxLength?: number; 
  };
}

interface QuestionnaireData {
  success: boolean;
  session: { 
    id: string; 
    status: string; 
    progress: number;
    createdAt: string;
    expiresAt: string;
  };
  template: { 
    id: string; 
    name: string; 
    description: string; 
    schema: Question[];
    type: string;
    requiresAdvisorReview: boolean;
  };
  party: { 
    id: string;
    firstName: string; 
    lastName: string; 
    displayName: string;
    email: string;
  };
  answers: Record<string, any>;
  consents: any[];
  metadata: { 
    answersCount: number; 
    requiredQuestions: number; 
    canResume: boolean;
    canSubmit: boolean;
  };
}

export default function QuestionnaireWizard() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<QuestionnaireData | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [isReviewStep, setIsReviewStep] = useState(false);

  // Auto-save functionality
  const { save, isSaving, lastSaved } = useAutoSave(
    async (answersToSave: Record<string, any>) => {
      if (!token || !data) return;

      try {
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/questionnaire-save`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            token,
            answers: answersToSave,
            currentStep: isReviewStep ? data.template.schema.length : currentStep,
            totalSteps: data.template.schema.length + 1,
            isSubmission: false
          })
        });

        const result = await response.json();
        if (!result.success) {
          throw new Error(result.error || 'Failed to save progress');
        }

        return result;
      } catch (error) {
        console.error('Auto-save failed:', error);
        throw error;
      }
    },
    1000 // Debounce delay
  );

  // Load questionnaire data
  useEffect(() => {
    if (!token) {
      setError('Invalid questionnaire link');
      setLoading(false);
      return;
    }

    loadQuestionnaire();
  }, [token]);

  const loadQuestionnaire = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/questionnaire-get/${token}`, {
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        }
      });

      const result = await response.json();
      
      if (!result.success) {
        if (result.expired) {
          setError('This questionnaire link has expired. Please request a new one from your advisor.');
        } else if (result.completed) {
          setError('This questionnaire has already been submitted. Thank you for your completion.');
        } else {
          setError(result.error || 'Failed to load questionnaire');
        }
        return;
      }

      setData(result);
      setAnswers(result.answers || {});
      
      // Determine current step based on answered questions
      const answeredQuestionIds = Object.keys(result.answers || {});
      const lastAnsweredIndex = result.template.schema.findLastIndex(q => 
        answeredQuestionIds.includes(q.id)
      );
      
      if (lastAnsweredIndex >= 0 && lastAnsweredIndex < result.template.schema.length - 1) {
        setCurrentStep(lastAnsweredIndex + 1);
      } else if (lastAnsweredIndex === result.template.schema.length - 1) {
        setIsReviewStep(true);
      }

    } catch (error) {
      console.error('Failed to load questionnaire:', error);
      setError('Failed to load questionnaire. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle answer changes
  const handleAnswerChange = (questionId: string, answer: any) => {
    const newAnswers = { ...answers, [questionId]: answer };
    setAnswers(newAnswers);
    
    // Trigger auto-save
    save(newAnswers);
  };

  // Navigate to next step
  const handleNext = () => {
    if (!data) return;

    if (currentStep < data.template.schema.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setIsReviewStep(true);
    }
  };

  // Navigate to previous step
  const handlePrevious = () => {
    if (isReviewStep) {
      setIsReviewStep(false);
      setCurrentStep(data!.template.schema.length - 1);
    } else if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Submit questionnaire
  const handleSubmit = async () => {
    if (!token || !data) return;

    try {
      setSubmitting(true);
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/questionnaire-save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          token,
          answers,
          currentStep: data.template.schema.length,
          totalSteps: data.template.schema.length + 1,
          isSubmission: true
        })
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to submit questionnaire');
      }

      // Show success message and redirect
      alert('Thank you! Your questionnaire has been submitted successfully. Your advisor will review your responses and contact you shortly.');
      
      // Redirect to a thank you page or close the window
      window.close();

    } catch (error) {
      console.error('Submit failed:', error);
      alert('Failed to submit questionnaire. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate progress
  const getProgress = () => {
    if (!data) return 0;
    const totalSteps = data.template.schema.length + 1; // +1 for review step
    const currentStepNumber = isReviewStep ? totalSteps : currentStep + 1;
    return Math.round((currentStepNumber / totalSteps) * 100);
  };

  // Get current question
  const getCurrentQuestion = () => {
    if (!data || isReviewStep) return null;
    return data.template.schema[currentStep];
  };

  // Check if current step is valid to proceed
  const canProceed = () => {
    const question = getCurrentQuestion();
    if (!question) return true;
    
    const answer = answers[question.id];
    if (question.required === false) return true;
    
    return answer !== undefined && answer !== null && answer !== '';
  };

  if (loading) {
    return <LoadingState message="Loading questionnaire..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Unable to Load Questionnaire</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.close()}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return <LoadingState message="Initializing questionnaire..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 relative overflow-hidden">
      {/* Background Pattern */}
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
          backgroundSize: '20px 20px'
        }}
      />
      
      <div className="relative z-10 container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 shadow-lg mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">{data.template.name}</h1>
          <p className="text-gray-300 text-lg">{data.template.description}</p>
          <p className="text-gray-400 mt-2">
            For: <span className="text-white font-medium">{data.party.displayName}</span>
          </p>
        </div>

        {/* Progress Indicator */}
        <ProgressIndicator
          currentStep={isReviewStep ? data.template.schema.length : currentStep}
          totalSteps={data.template.schema.length}
          progress={getProgress()}
          isReviewStep={isReviewStep}
        />

        {/* Auto-save indicator */}
        {(isSaving || lastSaved) && (
          <div className="flex items-center justify-center mb-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 flex items-center gap-2">
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                  <span className="text-white text-sm">Saving...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-white text-sm">
                    Saved {lastSaved ? new Date(lastSaved).toLocaleTimeString() : ''}
                  </span>
                </>
              )}
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden">
          {isReviewStep ? (
            <ReviewStep
              questions={data.template.schema}
              answers={answers}
              onEdit={(questionIndex) => {
                setIsReviewStep(false);
                setCurrentStep(questionIndex);
              }}
              onSubmit={handleSubmit}
              onPrevious={handlePrevious}
              submitting={submitting}
            />
          ) : (
            <QuestionStep
              question={getCurrentQuestion()!}
              answer={answers[getCurrentQuestion()?.id || '']}
              onAnswerChange={handleAnswerChange}
              onNext={handleNext}
              onPrevious={handlePrevious}
              canProceed={canProceed()}
              isFirst={currentStep === 0}
              isLast={currentStep === data.template.schema.length - 1}
              stepNumber={currentStep + 1}
              totalSteps={data.template.schema.length}
            />
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-gray-400 text-sm">
            Powered by <span className="text-white font-medium">PCM Financial</span> • 
            Secure & Encrypted • {data.metadata.answersCount} of {data.metadata.requiredQuestions} questions answered
          </p>
        </div>
      </div>
    </div>
  );
}